import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { type UseFormReturn } from "react-hook-form";
import { type FormSchema, discoveryOptions } from "../onboarding-schema";
import { HelpCircle, PenIcon } from "lucide-react";

interface DiscoveryStepProps {
  form: UseFormReturn<FormSchema>;
  showOtherDiscovery: boolean;
}

export function DiscoveryStep({ form, showOtherDiscovery }: DiscoveryStepProps) {
  return (
    <>
      <FormField
        control={form.control}
        name="discovery_source"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <FormLabel className="text-lg sm:text-xl md:text-2xl font-semibold block mb-2 sm:mb-3 bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
              How did you find us?
            </FormLabel>
            <div className="flex items-center mb-2 sm:mb-3 gap-1.5">
              <HelpCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-blue-400" />
              <p className="text-xs text-blue-100/60">This helps us understand our reach</p>
            </div>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value}
                className="grid grid-cols-1 sm:grid-cols-2 gap-2"
              >
                {discoveryOptions.map((option) => (
                  <FormItem key={option.id}>
                    <FormControl>
                      <label
                        htmlFor={option.id}
                        className={`block p-2.5 sm:p-3 rounded-lg border cursor-pointer transition-all duration-200 ${
                          field.value === option.id
                            ? "border-blue-400/50 bg-blue-500/10 shadow-md"
                            : "border-gray-700/80 hover:border-gray-600/80 hover:bg-gray-800/50 bg-gray-900/40"
                        }`}
                      >
                        <div className="flex items-start space-x-2 sm:space-x-2.5">
                          <RadioGroupItem 
                            value={option.id} 
                            id={option.id} 
                            className={`flex-shrink-0 mt-0.5 ${field.value === option.id ? "text-blue-400 border-blue-400" : "text-gray-500 border-gray-600"}`}
                          />
                          <span className={`text-xs sm:text-xs font-medium leading-tight text-left ${field.value === option.id ? "text-blue-50" : "text-gray-300"}`}>
                            {option.label}
                          </span>
                        </div>
                      </label>
                    </FormControl>
                  </FormItem>
                ))}
              </RadioGroup>
            </FormControl>
            <FormMessage className="text-sm text-red-400" />
          </FormItem>
        )}
      />
      {showOtherDiscovery && (
        <FormField
          control={form.control}
          name="other_discovery"
          render={({ field }) => (
            <FormItem className="mt-3 sm:mt-4">
              <FormLabel className="text-xs sm:text-sm font-medium text-blue-100/80 mb-2 block">Please tell us more</FormLabel>
              <FormControl>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    <PenIcon className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400" />
                  </div>
                  <Input
                    placeholder="Please tell us how you found Strykr"
                    className="pl-8 sm:pl-10 bg-gray-800/50 border-gray-700 h-9 sm:h-12 text-xs sm:text-sm focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200"
                    autoFocus
                    {...field}
                  />
                </div>
              </FormControl>
              <FormMessage className="text-sm text-red-400" />
            </FormItem>
          )}
        />
      )}
    </>
  );
}